/**
 * Zakladka "Podsumowanie miesięczne" (kontrakt GET /external-costs/summary/monthly?rok&location_id).
 * Kafelki: razem brutto / zaplacone / niezaplacone / przeterminowane. Macierz: wiersze = kategorie (albo sklepy),
 * kolumny = 12 miesiecy + Razem; miara do wyboru (brutto / netto / niezaplacone / przeterminowane).
 * Slupki miesieczne w czystym CSS (zaplacone = success, niezaplacone = warning, przeterminowane = danger) - bez bibliotek.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { externalCostsService } from '../../services/externalCostsService';
import { formatujKwote, komunikatBledu, etykietaMiesiaca } from './format';

const MIARY = [
  { id: 'brutto', etykieta: 'Brutto' },
  { id: 'netto', etykieta: 'Netto' },
  { id: 'niezaplacone', etykieta: 'Niezapłacone' },
  { id: 'przeterminowane', etykieta: 'Przeterminowane' },
];
const PODZIALY = [{ id: 'kategorie', etykieta: 'Kategorie' }, { id: 'sklepy', etykieta: 'Sklepy' }];

const pole = { padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' };
const przelacznik = (aktywny) => ({
  padding: '4px 10px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer',
  border: `1px solid ${aktywny ? 'var(--primary)' : 'var(--border)'}`,
  background: aktywny ? 'var(--primary)' : 'var(--card-bg)', color: aktywny ? '#fff' : 'var(--text-secondary)',
});
const th = { padding: '6px 8px', textAlign: 'right', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)' };
const td = { padding: '5px 8px', borderBottom: '1px solid var(--border)', fontSize: '11px', textAlign: 'right', whiteSpace: 'nowrap' };
const kwotaKrotka = (v) => (v === 0 || v === null || v === undefined) ? <span style={{ color: 'var(--text-muted)' }}>—</span> : formatujKwote(v);

const Kafelek = ({ testId, etykieta, wartosc, dopisek, kolor }) => (
  <div data-testid={testId} style={{ background: 'var(--card-bg)', padding: '10px 12px', borderRadius: '6px', borderLeft: `3px solid ${kolor}`, border: '1px solid var(--border)', borderLeftWidth: '3px', borderLeftColor: kolor }}>
    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{etykieta}</div>
    <div style={{ fontSize: '16px', fontWeight: 700, color: kolor }}>{formatujKwote(wartosc)}</div>
    {dopisek && <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{dopisek}</div>}
  </div>
);

export default function PodsumowanieMiesieczneTab({ sklepy = [], locationId = '' }) {
  const [rok, setRok] = useState(new Date().getFullYear());
  const [sklep, setSklep] = useState(locationId ? String(locationId) : '');
  const [miara, setMiara] = useState('brutto');
  const [podzial, setPodzial] = useState('kategorie');
  const [dane, setDane] = useState(null);
  const [ladowanie, setLadowanie] = useState(true);
  const [blad, setBlad] = useState(null);

  const laduj = useCallback(async () => {
    setLadowanie(true);
    try {
      const odp = await externalCostsService.getMonthlySummary(rok, sklep);
      setDane(odp?.data || null);
      setBlad(null);
    } catch (err) {
      if (!err?.isAuthError) setBlad(komunikatBledu(err, 'Nie udało się pobrać podsumowania.'));
    } finally {
      setLadowanie(false);
    }
  }, [rok, sklep]);

  useEffect(() => { laduj(); }, [laduj]);

  // wiersze macierzy: {klucz, nazwa, kolor, miesiace: {yyyy-mm: wartosc}, razem}
  const wiersze = useMemo(() => {
    if (!dane) return [];
    const grupy = {};
    const nazwaGrupy = (k) => podzial === 'kategorie'
      ? { klucz: String(k.category_id), nazwa: k.kategoria || `Kategoria #${k.category_id}`, kolor: k.kolor }
      : { klucz: k.location_id ? String(k.location_id) : 'ogolne', nazwa: k.sklep || 'Ogólne (centrala)', kolor: null };
    (dane.komorki || []).forEach(k => {
      const g = nazwaGrupy(k);
      const w = grupy[g.klucz] || (grupy[g.klucz] = { ...g, miesiace: {}, razem: 0, liczba: 0 });
      w.miesiace[k.miesiac] = Math.round(((w.miesiace[k.miesiac] || 0) + (k[miara] || 0)) * 100) / 100;
      w.razem = Math.round((w.razem + (k[miara] || 0)) * 100) / 100;
      w.liczba += k.liczba || 0;
    });
    return Object.values(grupy).sort((a, b) => (a.nazwa || '').localeCompare(b.nazwa || '', 'pl'));
  }, [dane, miara, podzial]);

  const miesiace = dane?.miesiace || [];
  const sumy = dane?.sumy_miesiac || {};
  const razem = dane?.razem || {};
  const maxSlupka = Math.max(1, ...miesiace.map(m => Math.abs(sumy[m]?.brutto || 0)));
  const lata = [rok - 2, rok - 1, rok, rok + 1].filter(r => r <= new Date().getFullYear() + 1);

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
        <select data-testid="pods-rok" value={rok} onChange={e => setRok(Number(e.target.value))} style={pole}>
          {lata.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select data-testid="pods-sklep" value={sklep} onChange={e => setSklep(e.target.value)} style={pole}>
          <option value="">Wszystkie sklepy + ogólne</option>
          <option value="0">Tylko ogólne (centrala)</option>
          {sklepy.map(s => <option key={s.id} value={s.id}>{s.nazwa}</option>)}
        </select>
        <div style={{ display: 'flex', gap: '4px' }}>
          {MIARY.map(m => <button key={m.id} type="button" data-testid={`pods-miara-${m.id}`} onClick={() => setMiara(m.id)} style={przelacznik(miara === m.id)}>{m.etykieta}</button>)}
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {PODZIALY.map(p => <button key={p.id} type="button" data-testid={`pods-podzial-${p.id}`} onClick={() => setPodzial(p.id)} style={przelacznik(podzial === p.id)}>{p.etykieta}</button>)}
        </div>
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-muted)' }}>{ladowanie ? 'Ładowanie…' : `${razem.liczba || 0} kosztów w ${rok}`}</span>
      </div>

      {blad && <div data-testid="pods-blad" style={{ fontSize: '11px', color: 'var(--danger)', marginBottom: '8px' }}>{blad}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '8px', marginBottom: '12px' }}>
        <Kafelek testId="pods-razem" etykieta="Razem brutto" wartosc={razem.brutto} dopisek={`netto ${formatujKwote(razem.netto)}`} kolor="var(--primary)" />
        <Kafelek testId="pods-zaplacone" etykieta="Zapłacone" wartosc={razem.zaplacone} kolor="var(--success)" />
        <Kafelek testId="pods-niezaplacone" etykieta="Niezapłacone" wartosc={razem.niezaplacone} kolor="var(--warning)" />
        <Kafelek testId="pods-przeterminowane" etykieta="Przeterminowane" wartosc={razem.przeterminowane} dopisek={`${razem.przeterminowane_liczba || 0} faktur po terminie`} kolor="var(--danger)" />
      </div>

      {/* Slupki miesieczne (CSS) */}
      <div data-testid="pods-slupki" style={{ display: 'grid', gridTemplateColumns: `repeat(${miesiace.length || 12}, 1fr)`, gap: '6px', alignItems: 'end', height: '120px', padding: '8px 10px 0', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '6px 6px 0 0' }}>
        {miesiace.map(m => {
          const s = sumy[m] || {};
          const h = (v) => `${Math.round(Math.abs(v || 0) / maxSlupka * 100)}%`;
          return (
            <div key={m} data-testid={`pods-slupek-${m}`} title={`${etykietaMiesiaca(m)} ${rok}: brutto ${formatujKwote(s.brutto)}, zapłacone ${formatujKwote(s.zaplacone)}, niezapłacone ${formatujKwote(s.niezaplacone)}, przeterminowane ${formatujKwote(s.przeterminowane)}`}
              style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
              <div style={{ height: h(s.przeterminowane), background: 'var(--danger)', minHeight: s.przeterminowane ? '2px' : 0 }} />
              <div style={{ height: h((s.niezaplacone || 0) - (s.przeterminowane || 0)), background: 'var(--warning)', minHeight: (s.niezaplacone || 0) - (s.przeterminowane || 0) > 0 ? '2px' : 0 }} />
              <div style={{ height: h(s.zaplacone), background: 'var(--success)', minHeight: s.zaplacone ? '2px' : 0 }} />
            </div>
          );
        })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${miesiace.length || 12}, 1fr)`, gap: '6px', padding: '4px 10px 8px', background: 'var(--card-bg)', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 6px 6px', marginBottom: '12px', fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center' }}>
        {miesiace.map(m => <div key={m}>{etykietaMiesiaca(m)}</div>)}
      </div>

      {/* Macierz */}
      <div style={{ overflow: 'auto', background: 'var(--card-bg)', borderRadius: '6px', border: '1px solid var(--border)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg)' }}>
              <th style={{ ...th, textAlign: 'left' }}>{podzial === 'kategorie' ? 'Kategoria' : 'Sklep'}</th>
              {miesiace.map(m => <th key={m} style={th}>{etykietaMiesiaca(m)}</th>)}
              <th style={th}>Razem</th>
            </tr>
          </thead>
          <tbody>
            {!ladowanie && wiersze.length === 0 && (
              <tr><td colSpan={miesiace.length + 2} style={{ ...td, textAlign: 'center', padding: '24px', color: 'var(--text-muted)' }}>Brak kosztów w wybranym roku.</td></tr>
            )}
            {wiersze.map(w => (
              <tr key={w.klucz} data-testid={`pods-wiersz-${w.klucz}`}>
                <td style={{ ...td, textAlign: 'left' }}>
                  {w.kolor && <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '2px', background: w.kolor, marginRight: '6px' }} />}
                  {w.nazwa} <span style={{ color: 'var(--text-muted)' }}>({w.liczba})</span>
                </td>
                {miesiace.map(m => <td key={m} style={td}>{kwotaKrotka(w.miesiace[m])}</td>)}
                <td style={{ ...td, fontWeight: 600 }}>{formatujKwote(w.razem)}</td>
              </tr>
            ))}
          </tbody>
          {wiersze.length > 0 && (
            <tfoot>
              <tr data-testid="pods-suma" style={{ background: 'var(--bg)' }}>
                <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>Razem</td>
                {miesiace.map(m => <td key={m} style={{ ...td, fontWeight: 600 }}>{kwotaKrotka(sumy[m]?.[miara])}</td>)}
                <td style={{ ...td, fontWeight: 700 }}>{formatujKwote(razem[miara])}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
